import { pb, escapeFilterValue } from '../lib/pocketbase'
import type { Note, MessageType, RelatedEntityType } from '../types'
import { ensureUserAgency } from '../utils/ensureAgency'

export type NotePayload = {
  body: string // Required
  message_type?: MessageType | null
  related_entity_type?: RelatedEntityType | null
  related_entity_id?: string | null
  player?: string | null
  deal?: string | null
  contract?: string | null
  pinned?: boolean
  is_archived?: boolean
  tags?: string[] | null
  author_id?: string | null
  agency_id?: string
}

export type PaginatedNotesResult = {
  items: Note[]
  page: number
  perPage: number
  totalItems: number
  totalPages: number
}

function mapNote(record: Record<string, any>): Note {
  return {
    id: record.id,
    body: record.body ?? '',
    message_type: record.message_type ?? null,
    related_entity_type: record.related_entity_type ?? null,
    related_entity_id: record.related_entity_id ?? null,
    player: record.player ?? null,
    deal: record.deal ?? null,
    contract: record.contract ?? null,
    pinned: record.pinned ?? false,
    is_archived: record.is_archived ?? false,
    tags: Array.isArray(record.tags) ? record.tags : null,
    author_id: record.author_id ?? null,
    agency_id: record.agency_id ?? '',
    created: record.created ?? '',
    updated: record.updated ?? '',
  }
}

export async function listNotes(filters?: {
  related_entity_type?: string
  related_entity_id?: string
  player?: string
  deal?: string
  contract?: string
}) {
  
  const filterParts: string[] = []

  // Agency filtering - only show notes from user's agency
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id
  if (agencyId) {
    filterParts.push(`agency_id = "${escapeFilterValue(agencyId)}"`)
  }

  // Support polymorphic relation filtering
  if (filters?.related_entity_type) {
    filterParts.push(`related_entity_type = "${escapeFilterValue(filters.related_entity_type)}"`)
  }
  if (filters?.related_entity_id) {
    filterParts.push(`related_entity_id = "${escapeFilterValue(filters.related_entity_id)}"`)
  }
  // Support direct relation filtering
  if (filters?.player) {
    filterParts.push(`player = "${escapeFilterValue(filters.player)}"`)
  }
  if (filters?.deal) {
    filterParts.push(`deal = "${escapeFilterValue(filters.deal)}"`)
  }
  if (filters?.contract) {
    filterParts.push(`contract = "${escapeFilterValue(filters.contract)}"`)
  }

  const filter = filterParts.length > 0 ? filterParts.join(' && ') : ''

  try {
    const data = await pb.collection('notes').getFullList<Record<string, any>>({
      filter,
      sort: '-created',
      expand: 'author_id,player,deal,contract',
    })
    return data.map(mapNote)
  } catch (error: any) {
    throw new Error(`Failed to load notes: ${error.message}`)
  }
}



export async function listNotesPaginated(
  page: number = 1,
  perPage: number = 25,
  options?: { sort?: string; filter?: string }
): Promise<PaginatedNotesResult> {
  
  // Agency filtering - only show notes from user's agency
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id
  const agencyFilter = agencyId ? `agency_id = "${escapeFilterValue(agencyId)}"` : ''
  const combinedFilter = options?.filter
    ? (agencyFilter ? `${agencyFilter} && ${options.filter}` : options.filter)
    : agencyFilter

  try {
    const result = await pb.collection('notes').getList<Record<string, any>>(page, perPage, {
      sort: options?.sort ?? '-created',
      ...(combinedFilter && { filter: combinedFilter }),
    })
    return {
      items: result.items.map(mapNote),
      page: result.page,
      perPage: result.perPage,
      totalItems: result.totalItems,
      totalPages: result.totalPages,
    }
  } catch (error: any) {
    throw new Error(`Failed to load notes: ${error.message}`)
  }
}

export async function getNote(noteId: string) {
  try {
    const record = await pb.collection('notes').getOne<Record<string, any>>(noteId, {
      expand: 'author_id,player,deal,contract',
    })
    return mapNote(record)
  } catch (error: any) {
    throw new Error(`Failed to load note: ${error.message}`)
  }
}

export async function createNote(payload: NotePayload) {
  // Ensure user has an agency assigned (auto-create if needed)
  const userAgencyId = await ensureUserAgency()
  const user = pb.authStore.model

  const createPayload: any = {
    ...payload,
    agency_id: userAgencyId,
    // Capture the author/creator if not already set
    author_id: payload.author_id || user?.id || '',
  }

  try {
    const record = await pb.collection('notes').create(createPayload)
    return mapNote(record)
  } catch (error: any) {
    throw new Error(`Failed to create note: ${error.message}`)
  }
}

export async function updateNote(
  noteId: string,
  payload: Partial<NotePayload>,
) {
  try {
    const record = await pb.collection('notes').update(noteId, payload)
    return mapNote(record)
  } catch (error: any) {
    throw new Error(`Failed to update note: ${error.message}`)
  }
}

export async function deleteNote(noteId: string) {
  try {
    await pb.collection('notes').delete(noteId)
  } catch (error: any) {
    throw new Error(`Failed to delete note: ${error.message}`)
  }
}

export async function toggleNotePin(noteId: string, pinned: boolean) {
  try {
    const record = await pb.collection('notes').update(noteId, { pinned })
    return mapNote(record)
  } catch (error: any) {
    throw new Error(`Failed to update note: ${error.message}`)
  }
}
